import * as React from 'react';

import type { Location } from 'history';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import type { NavigateFunction } from 'js/lib/useRouter';

import { sendMessage } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';

type UseAutoOpenCoachProps = {
  courseId: string;
  itemId: string;
  message: string;
  coachActionId: string;
  coachModeId: string;
};

/**
 * Utility function to remove the showCoach query parameter from the URL
 */
const removeShowCoachParam = (location: Location, navigate: NavigateFunction) => {
  const queryParams = new URLSearchParams(location.search);
  queryParams.delete('showCoach');
  const searchParams = queryParams.toString();
  navigate({ pathname: location.pathname, search: searchParams ? `?${searchParams}` : undefined }, { replace: true });
};

export const useAutoOpenCoachChatWithMessage = ({
  courseId,
  itemId,
  message,
  coachActionId,
  coachModeId,
}: UseAutoOpenCoachProps) => {
  const { showEmbeddedCoach } = useCoachEpicEnabledState();
  const { setToolSelected, setExpanded, expanded } = useCourseToolBarPlugins();
  const location = useLocation();
  const navigate = useNavigate();

  // Track when coach should auto-open with a message
  const [shouldAutoOpenCoach, setShouldAutoOpenCoach] = React.useState(false);
  const [isCoachMounted, setIsCoachMounted] = React.useState(false);

  // Check URL for showCoach parameter on component mount
  React.useEffect(() => {
    const shouldShowCoach = location.query?.showCoach === 'true';

    if (shouldShowCoach && showEmbeddedCoach) {
      setShouldAutoOpenCoach(true);
      // Open the coach toolbar first
      setToolSelected('coach');
      setExpanded(true);
    }
  }, [showEmbeddedCoach, setToolSelected, setExpanded, location.query?.showCoach]);

  // Handle when coach is mounted and ready
  const handleCoachMount = React.useCallback<() => void>(() => {
    setIsCoachMounted(true);
  }, []);

  // Send message when coach is ready and toolbar is expanded
  React.useEffect(() => {
    if (shouldAutoOpenCoach && isCoachMounted && expanded) {
      // Send the specified message
      sendMessage({
        input: {
          message,
          courseraContext: {
            courseId,
            itemId,
          },
          config: {
            coachActionId,
            coachModeId,
          },
        },
      });

      // Reset the flag so we don't send the message again
      setShouldAutoOpenCoach(false);

      removeShowCoachParam(location, navigate);
    }
  }, [
    shouldAutoOpenCoach,
    isCoachMounted,
    expanded,
    courseId,
    itemId,
    message,
    coachActionId,
    coachModeId,
    location,
    navigate,
  ]);

  return {
    handleCoachMount,
  };
};
